import { RequestMethod, Response } from '@angular/http';
import * as Q from 'q';
import { of } from 'rxjs/observable/of';
import { Subject } from 'rxjs/Subject';
import { Md5 } from 'ts-md5/dist/md5';
import { VASRestService } from '../../../core/services/api/vas-rest';
import { IUserService } from '../../../core/services/user-service.interface';
import { LoggedOutEvent } from '../../../core/system/system.actions';
import { User } from '../../business-objects/user/user.model';
import { IVasAppFacade } from '../vas-app-facade.service.interface';

export class UserService implements IUserService {

    busy$ = new Subject<boolean>();
    error$ = new Subject<Error>();

    constructor(
        protected appFacade: IVasAppFacade,
        protected restService: VASRestService
    ) {
    }

    /**
     * Handle the login process
     *
     * If successful the promise is resolved with the active user object, otherwise it is rejected with an error
     *
     * @param email
     * @param password
     * @returns Promise<User>
     */
    async login(email, password): Promise<User> {

        this.onBeforeLogin();

        const status = await this.appFacade.network.refreshStatusP();

        return status.ip ? this.loginOnline(email, password) : this.loginOffline(email, password);
    }

    /**
     * Handle the online login process
     * - post the login details to the server
     * - save the auth_token to local storage
     * - save the refresh_token to local storage
     * - get the user object from the server + update the password hash and save to local storage
     *
     * @param email
     * @param password
     * @returns {Promise<User>}
     */
    loginOnline(email: string, password: string): Promise<User> {
        console.log('logging in online');
        return new Promise((resolve, reject) => {
            [
                () => {
                    return new Promise((_resolve, _reject) => {
                        this.restService.postUnauthenticated('/login', {username: email, password}).subscribe(
                            (response: Response) => {
                                _resolve(response.json());
                            },
                            response => _reject(this.loginOnlineResponseToError(response))
                        );
                    });
                },
                body => {
                    return new Promise((_resolve, _reject) => {
                        this.appFacade.objects.config.store.setAuthToken(body.token);
                        _resolve(body);
                    });
                },
                body => {
                    return new Promise((_resolve, _reject) => {
                        this.appFacade.objects.config.store.setRefreshToken(body.refresh_token);
                        _resolve();
                    });
                },
                () => {
                    return new Promise((_resolve, _reject) => {
                        this.restService.get(`/users/email/${email}`, 'user').subscribe(
                            response => {
                                let user;

                                user = Object.keys(response.user.entities).map(key => response.user.entities[key]).pop();

                                if (!user) {
                                    _reject(new Error('GENERIC_ERROR'));
                                }

                                console.log('logged in');
                                user.passwordHash = Md5.hashStr(password);
                                this.appFacade.objects.user.store.insert(user);
                                this.appFacade.objects.user.store.setActive(user);
                                this.appFacade.network.refreshStatus();
                                _resolve(user);
                            },
                            response => {
                                _reject(this.loginOnlineResponseToError(response));
                            }
                        );
                    });
                }
            ].reduce<any>(Q.when, Q()).then(resolve, reject);
        });
    }

    /**
     * Handle the offline login process
     * - find the user in local storage by email
     * - if the password hash matches, success
     *
     * @param email
     * @param password
     * @returns {Promise<User>}
     */
    loginOffline(email: string, password: string): Promise<User> {
        console.log('logging in offline');
        return new Promise((resolve, reject) => {
            this.appFacade.objects.user.store.getAll()
                .map((users: Array<User>) => {
                    return users.filter((user: User) => user.email === email).pop();
                })
                .take(1)
                .subscribe(
                    (user: User) => {
                        if (user && Md5.hashStr(password) === user.passwordHash) {
                            this.setActiveUser(user);
                            resolve(user);
                        } else {
                            reject(new Error('INVALID_CREDENTIALS'));
                        }
                    }
                );
        });
    }

    /**
     * Handles a non-ok response from server during the online login process
     * @param response
     * @returns {Error}
     */
    loginOnlineResponseToError(response: Response): Error {
        switch (response.status) {
            case 401: {
                return new Error('INVALID_CREDENTIALS');
            }
            default: {
                return new Error('GENERIC_ERROR');
            }
        }
    }

    logout(): Promise<any> {
        this.appFacade.store.dispatch(new LoggedOutEvent());
        return Promise.resolve();
    }

    setActiveUser(user: User): void {
        this.appFacade.objects.user.store.setActive(user);
    }

    protected onBeforeLogin(): void {
        this.appFacade.objects.user.store.deActivate();
        this.appFacade.objects.account.store.deActivate();
    }
}



// WEBPACK FOOTER //
// ./src/vas/services/user/user.service.ts